import clsx from 'clsx';
import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useCurrentUser, useLayout, useView } from '@/hooks';
import { BottomBar, SideBar } from '@/ui';
import { clearCache } from '@/utils';

import LoadingCover from './Loading';
import MindsetBackground from './MindsetBackground';

import type { LayoutOutletContext } from './outletContext';

import EditClientAccount from '@/components/Profile/Client/Edit/Account';
import EditCoachAccount from '@/components/Profile/Coach/Edit/Account';
import Feedback from '@/components/Profile/Feedback';
import Widget from '@/components/Widget';
import McpAccountMenuItems from '@/features/mcpMember/account/McpAccountMenuItems';
import { useMcpMemberFlag } from '@/hooks/useMcpMemberFlag';
import { useLogoutUserMutation } from '@/store/api/authApi';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setChatId } from '@/store/slices/appSlice';

// TODO: Refactor into actual index instead of god layout
// TODO: Separate from components or move components related elsewhere
const Layout = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { user, isUserLoading } = useCurrentUser();
  const { isClient } = useView();
  const { menu, homePath, bottomMenu, moreBottomMenu } = useLayout();
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [isFeedback, setIsFeedback] = useState(false);
  const [logoutUser] = useLogoutUserMutation();

  const dispatch = useAppDispatch();
  const chatId = useAppSelector((state) => state.app.chatId);

  const isOnRawHomePath = pathname === homePath || pathname === `${homePath}/`;
  // The flagged Moments home is a plain page; skip the flag request everywhere
  // else so unflagged pages make no new request.
  const momentsHome = useMcpMemberFlag({
    skip: !(isClient && isOnRawHomePath),
  });
  const isOnHomePath = isOnRawHomePath && !momentsHome.enabled;
  const isOnCompanyPath = pathname.startsWith('/company');

  const containerClassnames =
    'mb-16 flex flex-1 flex-col overflow-x-hidden sm:mb-0';

  const onSignOut = async () => {
    try {
      await logoutUser().unwrap();
    } catch {
      // Continue with logout even if API call fails
    }
    clearCache();
    navigate('/signin');
  };

  const handleSetChatId = (val: string) => {
    dispatch(setChatId(val));
  };

  const onOpenFeedBack = () => setIsFeedback(true);
  const onCloseFeedBack = () => setIsFeedback(false);
  const onEditAccountSettings = () => setIsEditingDetails(true);
  const onCloseAccountSettings = () => setIsEditingDetails(false);
  const outletContext: LayoutOutletContext = {
    openAccountSettings: onEditAccountSettings,
  };
  const renderSideBarMcpItems = isClient
    ? (close: () => void) => (
        <McpAccountMenuItems variant="sidebar" onNavigate={close} />
      )
    : undefined;
  const renderBottomBarMcpItems = isClient
    ? (close: () => void) => (
        <McpAccountMenuItems variant="bottom" onNavigate={close} />
      )
    : undefined;

  if (isUserLoading) {
    return <LoadingCover />;
  }

  return (
    <div
      className={clsx('absolute inset-0 isolate flex overflow-auto', {
        'bg-sage-100': !isOnHomePath && !isClient,
        'bg-sage-600': isOnHomePath,
        'bg-sunny-100': !isOnHomePath && isClient && !isOnCompanyPath,
      })}
    >
      {isOnHomePath && <MindsetBackground mindset={user.mindset} />}
      <SideBar
        hideSideBar={false}
        isClient={isClient}
        isOnHomepage={isOnHomePath}
        menu={menu}
        renderAccountMenuItems={renderSideBarMcpItems}
        user={user}
        onAccountSettings={onEditAccountSettings}
        onFeedBack={onOpenFeedBack}
        onSignOut={onSignOut}
      />
      <main className={`${containerClassnames} sm:ml-18`}>
        <Outlet context={outletContext} />
      </main>
      <BottomBar
        menu={bottomMenu}
        moreMenu={moreBottomMenu}
        renderAccountMenuItems={renderBottomBarMcpItems}
        user={user}
        onAccountSettings={onEditAccountSettings}
        onFeedBack={onOpenFeedBack}
        onSignOut={onSignOut}
      />
      {isOnHomePath && (
        <div className="absolute inset-0 h-2/3 bg-gradient-to-b from-black/60 to-transparent" />
      )}

      {isEditingDetails && isClient && (
        <EditClientAccount client={user} onClose={onCloseAccountSettings} />
      )}
      {isEditingDetails && !isClient && (
        <EditCoachAccount coach={user} onClose={onCloseAccountSettings} />
      )}
      {isFeedback && <Feedback onClose={onCloseFeedBack} />}

      <Widget
        chatId={chatId}
        hideChat={false}
        sessionId={user.cometChatId}
        setChatId={handleSetChatId}
      />
    </div>
  );
};

export default Layout;
